import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'featured' | 'compact';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-all duration-200 cursor-pointer"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={movie.posterUrl}
            alt={`${movie.title} 电影海报`}
            title={`${movie.title} - 高清电影免费观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-50 truncate group-hover:text-primary-500 transition-colors">
            {movie.title}
          </h4>
          <p className="text-xs text-foreground-400 mt-0.5">{movie.year} · {movie.duration}</p>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-accent-500 text-xs font-semibold">{movie.rating}</span>
            <span className="text-foreground-500 text-xs">分</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group relative block rounded-xl overflow-hidden bg-background-100 cursor-pointer h-full"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={movie.heroUrl}
            alt={`${movie.title} 电影海报`}
            title={`${movie.title} - 高清电影免费在线观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-xl font-bold text-foreground-50 mb-1">{movie.title}</h3>
          <p className="text-sm text-foreground-300 line-clamp-2 mb-2">{movie.description}</p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-500/20 text-primary-400 text-xs font-semibold">
              <i className="ri-star-fill text-xs"></i> {movie.rating}
            </span>
            <span className="text-xs text-foreground-400">{movie.year}</span>
            <span className="text-xs text-foreground-400">{movie.duration}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-xl overflow-hidden bg-background-100 hover:bg-background-100/80 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[2/3] overflow-hidden relative">
        <img
          src={movie.posterUrl}
          alt={`${movie.title} 电影海报`}
          title={`${movie.title} - 高清电影免费在线看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {movie.isNew && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-950 text-xs font-semibold">
              NEW
            </span>
          )}
          {movie.isHot && (
            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
              热播
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-background-950/70 text-accent-500 text-xs font-bold">
          {movie.rating}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          <span className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap animate-scale-in">
            <i className="ri-play-fill mr-1"></i>立即观看
          </span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-50 truncate group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-foreground-400">{movie.year} · {movie.region}</span>
          <span className="text-xs text-foreground-500">{movie.duration}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {movie.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-400 text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}